import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { LOGO_URL } from "../lib/accents";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [libTarget, setLibTarget] = useState(null);
  const [libCount, setLibCount] = useState(0);

  useEffect(() => {
    api.get("/public/library-count")
      .then((r) => {
        const c = r.data.count ?? 100;
        setLibTarget(c < 10 ? c : Math.floor(c / 10) * 10);
      })
      .catch(() => setLibTarget(100));
  }, []);

  useEffect(() => {
    if (libTarget == null || libTarget <= 0) return;
    let start = null;
    const duration = 1200; // ms
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setLibCount(Math.round(libTarget * eased));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [libTarget]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      <div className="hidden lg:flex w-1/2 bg-[#000000] flex-col justify-between p-14">
        <img src={LOGO_URL} alt="UnCloned®" className="h-8 w-auto self-start" />
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#0FFFE2]">The Ads Lookbook</p>
          <h1 className="font-display font-black text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.02] mt-5">
            <span className="whitespace-nowrap">Access <span className="text-[#DF1283] tabular-nums" data-testid="hero-library-count">{libTarget == null ? "100" : libCount}+</span> Ad</span><br />
            Creatives
            <span className="block font-display font-medium text-[26px] lg:text-[30px] xl:text-4xl text-white/75 mt-4 leading-[1.15] tracking-tight">
              for your next <span className="italic font-semibold text-[#D0FF0D] whitespace-nowrap">scroll-stopping</span><br />
              marketing campaign.
            </span>
          </h1>
          <p className="text-gray-400 mt-8 max-w-lg leading-relaxed">
            A curated vault of real-world ad creatives built to spark bigger ideas, sharpen your strategy, and help you create campaigns that are impossible to ignore. Study what stops the scroll, uncover what makes it work, then use that inspiration to build ads that look, sound, and sell like you.
          </p>
        </div>
        <div className="flex gap-2">
          {["#0FFFE2", "#DF1283", "#D0FF0D", "#4A0A66", "#FC861E"].map((c) => (
            <div key={c} className="w-8 h-2 rounded-full" style={{ background: c }} />
          ))}
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md fade-up text-center">
          <img src={LOGO_URL} alt="UnCloned®" className="lg:hidden w-72 h-auto mx-auto mb-3" />
          <h2 className="font-display font-black text-5xl lg:text-4xl text-[#000000]">Welcome Back</h2>
          <p className="text-gray-500 mt-3 text-sm">Sign in to access your ad library.</p>
          <form onSubmit={submit} className="mt-8 space-y-4 text-left">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Email</label>
              <input
                data-testid="login-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2] focus:border-transparent"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Password</label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs font-bold text-[#DF1283] hover:underline"
                >
                  Forgot?
                </Link>
              </div>
              <input
                data-testid="login-password-input"
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2] focus:border-transparent"
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="login-submit-btn"
              type="submit" disabled={loading}
              className="w-full bg-[#000000] text-white rounded-full px-6 py-3.5 font-bold text-sm tracking-[0.08em] hover:bg-[#DF1283] transition-colors duration-200 disabled:opacity-60"
            >
              {loading ? "SIGNING IN..." : "SIGN IN"}
            </button>
          </form>
          <div className="mt-8 border-t border-gray-100 pt-6">
            <p className="text-sm text-gray-500">
              Have an access code?{" "}
              <Link to="/register" data-testid="login-register-link" className="font-bold text-[#DF1283] hover:underline">
                Create your account
              </Link>
            </p>
            <p className="text-xs text-gray-400 mt-3">Access is members-only. Contact your UnCloned® account manager if you need an invitation or access code.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
